import type { OpenAPISpec } from '@/types/openapi';

type Json = Record<string, unknown>;

const METHODS = ['get', 'put', 'post', 'delete', 'patch', 'options', 'head', 'trace'];

// buildSaasSpec returns a copy of spec with only the x-audience: saas
// operations and the components they reach. The input is not modified.
export function buildSaasSpec(spec: OpenAPISpec): OpenAPISpec {
  const out = structuredClone(spec) as unknown as Json & {
    paths: Record<string, Json>;
    components?: Record<string, Record<string, unknown>>;
    tags?: { name: string }[];
  };

  const paths: Record<string, Json> = {};
  for (const [path, item] of Object.entries(out.paths)) {
    const kept: Json = {};
    let operations = 0;
    for (const [key, value] of Object.entries(item)) {
      if (!METHODS.includes(key)) {
        kept[key] = value; // path-level parameters, summary, servers
      } else if ((value as Json)['x-audience'] === 'saas') {
        kept[key] = value;
        operations++;
      }
    }
    if (operations > 0) paths[path] = kept;
  }
  out.paths = paths;

  // Follow $refs transitively: a kept schema may reference another one.
  const components = out.components ?? {};
  const reachable = new Set<string>();
  const queue: unknown[] = [paths];
  while (queue.length > 0) {
    const node = queue.pop();
    if (Array.isArray(node)) {
      queue.push(...node);
    } else if (node && typeof node === 'object') {
      for (const [key, value] of Object.entries(node)) {
        if (key === '$ref' && typeof value === 'string') {
          if (value.startsWith('#/components/')) {
            // A deep ref (#/components/schemas/A/properties/b) keeps all of A.
            const [, , section, name] = value.split('/');
            const key = `#/components/${section}/${name}`;
            if (!reachable.has(key)) {
              reachable.add(key);
              queue.push(components[section]?.[name]);
            }
          }
        } else {
          queue.push(value);
        }
      }
    }
  }
  for (const [section, entries] of Object.entries(components)) {
    if (section === 'securitySchemes') continue; // referenced by name, not $ref
    for (const name of Object.keys(entries)) {
      if (!reachable.has(`#/components/${section}/${name}`)) delete entries[name];
    }
  }

  const usedTags = new Set(
    Object.values(paths).flatMap((item) =>
      METHODS.flatMap((m) => ((item[m] as Json | undefined)?.tags as string[] | undefined) ?? []),
    ),
  );
  if (out.tags) out.tags = out.tags.filter((t) => usedTags.has(t.name));

  return out as unknown as OpenAPISpec;
}
